'use client';

import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Category } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { oneRowSliderLiveCasinoParams } from '@/helpers/games';
import { getSliderParams } from '@/helpers/getSliderParams';
import { useResponsive } from '@/modules/client';
import { useGetCommonStore } from '@/store';
import { GamesSlider } from '../GamesSlider';

export const GamesSliderExclusive: FC = () => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();
  const { currentMediaType } = useResponsive();

  const { exclusiveGames, fetchGames, shouldWaitForUser } = useCommonStore(
    useShallow((state) => ({
      exclusiveGames: state.gamesByCategory?.[Category.EXCLUSIVE],
      fetchGames: state.fetchGames,
      // fetchGames reads currencies from the wallets, which SSR does not
      // hydrate: fetching earlier sends a logged-in player's request with none.
      shouldWaitForUser:
        state.pendingWallets ||
        (state.isAuthenticated &&
          !state.wallets.length &&
          state.reasonForUpdatingUser === undefined),
    }))
  );

  useEffect(() => {
    if (exclusiveGames !== undefined || shouldWaitForUser) return;

    let isCancelled = false;

    fetchGames({
      filter: {
        categories: {
          identifiers: [Category.EXCLUSIVE],
        },
      },
      pageSize: 21,
      page: 1,
    }).then((response) => {
      if (isCancelled) return;

      // null marks an empty or failed response as loaded, so it is not
      // refetched on every store update.
      useCommonStore.setState((state) => ({
        gamesByCategory: {
          ...state.gamesByCategory,
          [Category.EXCLUSIVE]: response ?? null,
        },
      }));
    });

    return () => {
      isCancelled = true;
    };
  }, [exclusiveGames, fetchGames, shouldWaitForUser, useCommonStore]);

  if (exclusiveGames === undefined) {
    const { cols, rows } = getSliderParams(
      oneRowSliderLiveCasinoParams,
      currentMediaType
    );

    return <GamesSkeleton cols={cols} rows={rows} withTitle />;
  }

  return (
    <GamesSlider
      categoryId={Category.EXCLUSIVE}
      config={oneRowSliderLiveCasinoParams}
      dataTestId="exclusive-games-slider"
      icon={
        <ImageComponent
          alt="icon"
          height={24}
          src="/exclusiveIcon.webp"
          width={24}
        />
      }
      title={t(`common:gameCategories:${Category.EXCLUSIVE}`)}
    />
  );
};
